import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getActiveTemplates } from "@/services/templates";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface CardTemplate {
  id: string;
  name: string;
  image_url: string;
  occasion: string;
}

const occasionMeta: Record<string, { title: string; description: string }> = {
  anniversary: {
    title: "Anniversary Cards Signed by the Group",
    description: "Celebrate their love with a group card signed by everyone. Add heartfelt messages, funny memories, and photos.",
  },
  baby: {
    title: "Baby Shower & New Baby Cards",
    description: "Welcome the newest addition with a group card full of love, advice, and well-wishes from the whole crew.",
  },
  birthday: {
    title: "Birthday Cards Signed by the Group",
    description: "Make their birthday unforgettable with a group card everyone can sign. Add messages, GIFs, and photos.",
  },
  christmas: {
    title: "Christmas & Holiday Cards",
    description: "Spread festive cheer with a group Christmas card. Perfect for teams, families, and friend groups.",
  },
  farewell: {
    title: "Farewell Cards Signed by the Group",
    description: "Give them a proper send-off with a group farewell card filled with memories, well-wishes, and inside jokes.",
  },
  "get-well": {
    title: "Get Well Soon Cards",
    description: "Brighten their day with a group get well card full of encouraging messages and warm wishes.",
  },
  graduation: {
    title: "Graduation Cards Signed by the Group",
    description: "Celebrate their achievement with a group graduation card full of congratulations and proud messages.",
  },
  retirement: {
    title: "Retirement Cards Signed by the Group",
    description: "Honour their career with a group retirement card filled with memories, gratitude, and best wishes.",
  },
  sympathy: {
    title: "Sympathy & Condolence Cards",
    description: "Show you care with a group sympathy card. Let everyone add their heartfelt condolences and support.",
  },
  teacher: {
    title: "Teacher Appreciation Cards",
    description: "Thank an amazing teacher with a group card signed by students, parents, and colleagues.",
  },
  "thank-you": {
    title: "Thank You Cards Signed by the Group",
    description: "Express gratitude together with a group thank you card everyone can sign.",
  },
  valentines: {
    title: "Valentine's Day Cards",
    description: "Share the love with a Valentine's Day group card full of sweet messages and fun GIFs.",
  },
  wedding: {
    title: "Wedding Cards Signed by the Group",
    description: "Congratulate the happy couple with a group wedding card filled with love and best wishes.",
  },
};

const BrowseCards = () => {
  const { occasion } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<CardTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const meta = occasion ? occasionMeta[occasion] : null;
  const displayTitle = meta?.title || `${occasion?.replace(/-/g, " ")} Cards`;
  const displayDesc = meta?.description || "Browse and pick the perfect card design for your group.";

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      const { data } = await getActiveTemplates(occasion);
      setTemplates(data);
      setLoading(false);
    };
    fetchTemplates();
  }, [occasion]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="bg-muted/40">
        <div className="container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground leading-tight mb-4 capitalize">
              {displayTitle}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
              {displayDesc}
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/create")}
              className="rounded-full px-8 gap-2 bg-foreground text-background hover:bg-foreground/90"
            >
              Start
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* Breadcrumbs */}
      <div className="container mx-auto px-4 py-4">
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">
            All Cards
          </Link>
          <ChevronRight size={14} />
          <span className="text-foreground capitalize">
            {occasion?.replace(/-/g, " ")} Cards
          </span>
        </nav>
      </div>

      {/* Card Grid */}
      <div className="container mx-auto px-4 pb-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-4">No cards found for this occasion yet.</p>
            <Button variant="outline" onClick={() => navigate("/create")} className="rounded-full">
              Create a Custom Card
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => navigate("/create")}
                className="group text-left"
              >
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-border bg-muted mb-3 transition-shadow group-hover:shadow-lg">
                  <img
                    src={tpl.image_url}
                    alt={tpl.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {tpl.name}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BrowseCards;
